import { getFullPathName } from './common';
import { reviveFromJson } from './functions';

/* eslint-disable @typescript-eslint/no-namespace */
export namespace ApiClient {
  export const globalConfig: {
    beforeRedirect?: () => void;
  } = {};

  const defaultRequestHeaders = {
    'X-Requested-With': 'XMLHttpRequest',
  };

  export const ContentFormat = {
    Json: 'json',
    Form: 'form',
    MultipartForm: 'multipart-form',
    Text: 'text',
  } as const;

  export type ContentFormat = typeof ContentFormat[keyof typeof ContentFormat];

  interface ClientConfig {
    shouldRedirectWhenUnauthorized?: boolean;
    beforeRedirect?: () => void | boolean;
    useCommonResultParser?: boolean;
  }

  /* eslint-disable @typescript-eslint/no-empty-interface */
  export interface GetRequestConfig extends ClientConfig {}

  /** @throws {RequestError} */
  export const get = async <T>(path: string, config?: GetRequestConfig): Promise<T> => {
    const target = getFullPathName(path);

    const init: RequestInit = {
      method: 'GET',
      headers: {
        ...defaultRequestHeaders,
      },
      mode: 'same-origin',
      credentials: 'same-origin',
      cache: 'no-cache',
    };

    try {
      return await wrapRequest<T>(fetch(target, init), config);
    } catch (error) {
      if (error instanceof RequestError) {
        console.error(`ApiClient has detected an error on the GET request for "${target}".`, error);

        throw error;
      }

      throw new RequestError(String(error));
    }
  };

  export interface PostRequestConfig extends ClientConfig {
    contentFormat?: ContentFormat;
  }

  /** @throws {RequestError} */
  export const post = async <T>(path: string, data: unknown, config?: PostRequestConfig): Promise<T> => {
    const target = getFullPathName(path);

    const contentFormat = config?.contentFormat ?? getContentFormat(data);

    const init: RequestInit = {
      method: 'POST',
      headers: {
        ...defaultRequestHeaders,
        'Content-Type': getContentType(contentFormat),
      },
      mode: 'same-origin',
      credentials: 'same-origin',
      cache: 'no-cache',
      body: formatData(data, contentFormat),
    };

    // NOTE:
    // https://muffinman.io/blog/uploading-files-using-fetch-multipart-form-data/
    // > Remove 'Content-Type' header to allow browser to add along with the correct 'boundary'
    if (contentFormat === ContentFormat.MultipartForm) {
      delete (init.headers as any)?.['Content-Type'];
    }

    try {
      return await wrapRequest<T>(fetch(target, init), config);
    } catch (error) {
      if (error instanceof RequestError) {
        console.error(`ApiClient has detected an error on the POST request for "${target}".`, error);

        throw error;
      }

      throw new RequestError(String(error));
    }
  };

  const wrapRequest = <T>(request: Promise<Response>, config?: ClientConfig): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      request.then(
        (response) => {
          if (response.ok) {
            response.text().then(
              (contentText) => {
                let content: T;

                try {
                  content = JSON.parse(contentText, reviveFromJson);
                } catch (error) {
                  reject(new RequestError(String(error), response));

                  return;
                }

                if ((config?.useCommonResultParser ?? true) && isCommonResult(content)) {
                  const result = content;

                  if (result.status === 'success') {
                    resolve(result.data);
                  } else if (result.status === 'error') {
                    reject(new RequestError(String(result.message), response, content));
                  } else {
                    console.warn(
                      `The server responded with a status of ${result.status}. URL: ${response.url}`,
                      result
                    );

                    if (result.data ?? false) {
                      resolve(result.data);
                    } else {
                      reject(new RequestError(String(result.message), response, content));
                    }
                  }
                } else {
                  resolve(content);
                }
              },
              (error) => {
                reject(new RequestError(String(error), response));
              }
            );
          } else if (response.status === 401) {
            if (config?.shouldRedirectWhenUnauthorized ?? true) {
              if (config?.beforeRedirect) {
                let cancel = false;

                try {
                  cancel = !config.beforeRedirect() ?? false;
                } catch (error) {
                  console.error(error);
                }

                if (cancel) {
                  reject(new RequestError(response.statusText, response));

                  return;
                }
              }

              if (globalConfig.beforeRedirect) {
                try {
                  globalConfig.beforeRedirect();
                } catch (error) {
                  console.error(error);
                }
              }

              console.info(
                'ApiClient will redirect the current page to Login because it has received an Unauthorized response.'
              );

              const params = new URLSearchParams({
                ReturnUrl: `${location.pathname}${location.search}`,
              });

              location.href = getFullPathName(`/Login?${params}`);
            }
          } else {
            response.text().then(
              (contentText) => {
                if (contentText) {
                  try {
                    const content = JSON.parse(contentText, reviveFromJson);

                    reject(new RequestError(response.statusText, response, content));

                    return;
                  } catch {
                    // Nothing
                  }
                }

                reject(new RequestError(response.statusText, response));
              },
              (error) => {
                reject(new RequestError(String(error), response));
              }
            );
          }
        },
        (error) => {
          reject(new RequestError(String(error)));
        }
      );
    });
  };

  const getContentFormat = (data: unknown): ContentFormat => {
    if (data instanceof URLSearchParams) {
      return ContentFormat.Form;
    } else if (data instanceof FormData) {
      return ContentFormat.MultipartForm;
    }

    return ContentFormat.Json;
  };

  const getContentType = (contentFormat: ContentFormat): string => {
    if (contentFormat === ContentFormat.Form) {
      return 'application/x-www-form-urlencoded';
    } else if (contentFormat === ContentFormat.MultipartForm) {
      return 'multipart/form-data';
    } else if (contentFormat === ContentFormat.Text) {
      return 'text/plain';
    }

    return 'application/json';
  };

  const formatData = (data: unknown, contentFormat: ContentFormat): BodyInit => {
    if (contentFormat === ContentFormat.Form) {
      return data instanceof URLSearchParams ? data : new URLSearchParams(data as any);
    } else if (contentFormat === ContentFormat.MultipartForm) {
      return data instanceof FormData ? data : new FormData(data as any);
    } else if (contentFormat === ContentFormat.Text) {
      return String(data);
    }

    return JSON.stringify(data);
  };

  const isCommonResult = (arg: unknown): arg is IJsonResponse => {
    return (
      arg !== undefined &&
      typeof (arg as IJsonResponse).status === 'string' &&
      (arg as IJsonResponse).message !== undefined &&
      (arg as IJsonResponse).data !== undefined
    );
  };

  export class RequestError extends Error {
    readonly status?: number;
    readonly content?: unknown;

    constructor(message: string, response?: Response, content?: unknown) {
      super(message || 'An unknown error occurred.');

      this.name = this.constructor.name;

      if (Error.captureStackTrace) {
        Error.captureStackTrace(this, RequestError);
      }

      this.status = response?.status;
      this.content = content;
    }

    toString(): string {
      return this.message;
    }
  }
}
